import { useMemo } from 'react';
import { DdbEngagementType } from '@/lib/graphql/types-and-hooks';
import { toCallRow, type CallRow } from '@/lib/utils/call-rows';
import { useEngagementsBase } from './use-engagements-base';

export type CallRecordType = 'contact' | 'account' | 'deal';

const FILTER_KEY: Record<CallRecordType, string> = {
  contact: 'contactId',
  account: 'accountId',
  deal: 'dealId',
};

// Preview-mode sample calls (raw engagement shape, mapped like real data).
const dummyCalls = [
  {
    id: 'preview-call-1',
    displayName: 'Discovery call',
    description: '<p>Walked through current process and pricing. Follow up with a proposal.</p>',
    startTime: '2026-02-26T15:00:00',
    metadata: {
      direction: 'OUTBOUND',
      disposition_label: 'Connected',
      duration_ms: 1265000,
      recording_url: 'https://example.com/recording.mp3',
      from_number: '+1 512 555 0100',
      to_number: '+1 512 555 0199',
    },
  },
  {
    id: 'preview-call-2',
    displayName: '',
    description: '',
    startTime: '2026-02-24T10:30:00',
    metadata: { direction: 'OUTBOUND', disposition_label: 'Left voicemail', duration_ms: 42000 },
  },
];

interface UseRecordCallsOptions {
  recordType: CallRecordType;
  recordId: string;
  skip?: boolean;
  pageSize?: number;
}

/**
 * CALL engagements (HubSpot call sync-forward) linked to a contact, account or
 * deal, mapped to display rows. Pagination and preview mode come from
 * useEngagementsBase, the same stack as the Emails/Meetings tabs.
 */
export function useRecordCalls({ recordType, recordId, skip = false, pageSize = 20 }: UseRecordCallsOptions) {
  const entityFilter = useMemo(() => ({ [FILTER_KEY[recordType]]: recordId }), [recordType, recordId]);
  const { engagements, loading, error, hasMore, loadMore } = useEngagementsBase({
    entityFilter,
    entityId: recordId,
    type: DdbEngagementType.Call,
    pageSize,
    skip,
    dummyMeetings: [],
    dummyEmails: [],
    dummyCalls,
  });
  const calls: CallRow[] = useMemo(
    () => (engagements as Record<string, unknown>[]).map(toCallRow),
    [engagements],
  );
  return { calls, loading, error, hasMore, loadMore };
}
